// API configuration for webcomponents

const DEFAULT_API_URL = "https://api.baluu.io";

export function getApiUrl(): string {
  // Check for Vite env variable first (dev mode)
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return DEFAULT_API_URL;
}

export type CourseWithBundlesResponse = {
  course: Course;
  bundles: Bundle[];
  school: School;
  locations: Location[];
  ticketTypes: TicketType[];
  unavailableTimeBlocks: TimeBlock[];
  allAddOns: AddOn[];
  tutors: Tutor[];
  bundle: Bundle | null;
};

export type Course = {
  _id: string;
  title: string;
  thumbnail?: string;
  currency: string;
  isOnline: boolean;
  type: string;
  summary: CourseSummary;
  groupScheduleBriefing?: GroupScheduleBriefing;
  appointmentScheduleBriefing?: AppointmentScheduleBriefing;
};

export type CourseSummary = {
  bookingOptions: string[];
  hasFullTermDates?: boolean;
  hasProRataTermDates?: boolean;
  hasTrialDates?: boolean;
  hasDropInDates?: boolean;
  hasGroupScheduleDates?: boolean;
  hasAppointmentDates?: boolean;
  fullTermPrice?: number;
  proRataPrice?: { price: number };
  trialPrice?: number;
  dropInPrice?: number;
  numberOfTermlySessions?: number;
  numberOfAdditionalProRataDates?: number;
  additionalFullTermPrices?: number[];
  termName?: string;
  hasMultipleBookableTerms?: boolean;
  primaryTermBookableSessions?: number;
  primaryTermTotalSessions?: number;
  primaryTermIsProRata?: boolean;
  bookableTermsCount?: number;
};

export type GroupScheduleBriefing = {
  hasAvailability: boolean;
  price?: number;
  isFromPrice?: boolean;
};

export type AppointmentScheduleBriefing = {
  hasAvailability: boolean;
  price?: number;
  isFromPrice?: boolean;
};

export type Bundle = {
  id: string;
  name: string;
  price: number;
  sessions: number;
};

export type School = {
  _id: string;
  name: string;
  preferredCurrency: string;
  timezone: string;
  requireLoginToBook?: boolean;
};

export type Location = {
  _id: string;
  name: string;
  address: string;
};

export type TicketType = {
  _id: string;
  name: string;
  price: number;
  bookingType: string;
};

export type TimeBlock = {
  start: string;
  end: string;
};

export type AddOn = {
  id: string;
  name: string;
  price: number;
};

export type Tutor = {
  _id: string;
  name: string;
};

// Timetable types
export type TimetableResponse = {
  upcomingCoursesIds: string[];
  courses: TimetableCourse[];
  distinctions: TimetableDistinctions;
  locations: TimetableLocation[];
  sessions: TimetableSession[];
  fallbackSessions: TimetableSession[];
  total: number;
  tutors: TimetableTutor[];
  ticketTypes: TimetableTicketType[];
  availableBundles: TimetableBundle[];
};

export type TimetableTag = {
  _id: string;
  name: string;
  slug?: string;
};

export type TimetableCourse = {
  _id: string;
  schoolId: string;
  title: string;
  slug: string;
  type: string;
  currency: string;
  timezone: string;
  thumbnail?: string;
  blockBookingEnabled?: boolean;
  displaySettings?: {
    hideDisplaysAvailableTickets?: boolean;
  };
  tags?: TimetableTag[];
  fixedTags?: TimetableTag[];
};

export type TimetableSession = {
  _id: string;
  courseID: string;
  // Optional custom title for this session
  title?: string;
  startDateTime: string;
  endDateTime: string;
  duration: number;
  availability: number;
  capacity: number;
  locationId?: string;
  tutor?: string;
  number: number;
  numberOfSessions: number;
  isTerm: boolean;
  // Present on term sessions — the term's Event id, used to select the term's
  // first session at checkout.
  eventId?: string;
  isTrialEnabled?: boolean;
  visibility?: string;
  cancelled?: boolean;
  fallbackSessionId?: string;
  bookingOptions: TimetableBookingOption[];
  course: TimetableCourse;
};

export type TimetableBookingOption = {
  type: string;
  price: number;
  ticketTypeId?: string;
};

export type TimetableLocation = {
  _id: string;
  name: string;
  address?: string;
};

export type TimetableTutor = {
  _id: string;
  fullName?: string;
  name?: string;
  profileImage?: string;
  profileUrl?: string;
};

export type TimetableTicketType = {
  _id: string;
  name: string;
  price: number;
  bookingType: string;
  courseId: string;
};

export type TimetableDistinctions = {
  coursesIds: string[];
  excludedCoursesIds: string[];
  daysOfWeek: string[];
  locationsIds: string[];
  tutorsIds: string[];
};

export type TimetableBundle = {
  id: string;
  name: string;
  creditAmount: number;
  price: number;
  status: string;
};

export type TimetableFiltersParams = {
  coursesIds?: string[];
  locationIds?: string[];
  tutorIds?: string[];
  from?: string;
  to?: string;
  availability?: string;
  page?: number;
  limit?: number;
  /** Only these classes / the classes with these tags (comma-separated
   * ids): the scope the API resolves; the filters stay within it. */
  onlyCourseIds?: string;
  onlyTagIds?: string;
};

export async function fetchCourseWithBundles(
  courseId: string,
  apiUrl?: string,
): Promise<CourseWithBundlesResponse> {
  const baseUrl = apiUrl || getApiUrl();
  // Widget routes are mounted under /widget prefix in the API
  const response = await fetch(
    `${baseUrl}/widget/courses/${courseId}/with-bundles`,
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch course data: ${response.statusText}`);
  }

  return response.json();
}

export type CourseSummaryLite = {
  isSoldOut: boolean;
  isActive: boolean;
  bookingOptions: string[];
};

/**
 * Lightweight bookable/sold-out state for a single course — used by the Book Now
 * button to show "Join Waiting List" instead of "Book Now" when a course is full
 * or held by live queue offers, without pulling the heavy with-bundles payload.
 * Server-cached (~5 min), so it reflects offers with up to the cache TTL of lag.
 */
export async function fetchCourseSummary(
  courseId: string,
  apiUrl?: string,
): Promise<CourseSummaryLite> {
  const baseUrl = apiUrl || getApiUrl();
  const response = await fetch(
    `${baseUrl}/widget/courses/${courseId}/summary`,
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch course summary: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch timetable data for a school
 * Uses the same endpoint as the widget: /widget/pages/timetable/:slug
 */
export async function fetchTimetable(
  schoolSlug: string,
  params: TimetableFiltersParams = {},
  apiUrl?: string,
): Promise<TimetableResponse> {
  const baseUrl = apiUrl || getApiUrl();

  // Build query string
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.set("page", String(params.page));
  if (params.limit) queryParams.set("limit", String(params.limit));
  if (params.from) queryParams.set("from", params.from);
  if (params.to) queryParams.set("to", params.to);
  if (params.availability && params.availability !== "all") {
    queryParams.set("availability", params.availability);
  }

  // Handle array params
  if (params.coursesIds?.length) {
    params.coursesIds.forEach((id) => queryParams.append("coursesIds[]", id));
  }
  if (params.locationIds?.length) {
    params.locationIds.forEach((id) => queryParams.append("locationIds[]", id));
  }
  if (params.tutorIds?.length) {
    params.tutorIds.forEach((id) => queryParams.append("tutorIds[]", id));
  }
  if (params.onlyCourseIds) queryParams.set("onlyCourseIds", params.onlyCourseIds);
  if (params.onlyTagIds) queryParams.set("onlyTagIds", params.onlyTagIds);

  // Default range to custom for date filtering
  queryParams.set("range", "custom");

  const queryString = queryParams.toString();
  const url = `${baseUrl}/widget/pages/timetable/${schoolSlug}${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch timetable data: ${response.statusText}`);
  }

  return response.json();
}

